import { computed, Signal, signal, WritableSignal } from "@angular/core";
import { AbstractControl, FormGroup, StatusChangeEvent, TouchedChangeEvent } from "@angular/forms";
import { AppHelper } from "@shared-helpers/appHelper";
import { SubscriptionTracker } from "@shared-helpers/subscriptionTracker";
import { filter, Subscription } from "rxjs";

//@Injectable() // Instance per class instance
export class FormHelper {
  private form: FormGroup = null;
  formErrorMessages: WritableSignal<string[]> = signal([], { equal: AppHelper.objectsEqual });
  validationSummaryCssClass: Signal<string> = computed(() => this.formErrorMessages()?.length > 0 ? "validation-summary-errors" : "validation-summary-valid", { equal: AppHelper.objectsEqual });

  constructor() { }

  public initialise(formGroup: FormGroup, subscriptionTracker: SubscriptionTracker) {
    this.form = formGroup;

    if (this.form) {
      let subscription: Subscription = this.form.events
        .pipe(filter((event) => event instanceof TouchedChangeEvent || event instanceof StatusChangeEvent))
        .subscribe((event) => this.calculateValidationSummary(false));

      if (subscriptionTracker) {
        subscriptionTracker.add(subscription);
      }
    }
  }

  calculateValidationSummary(showAllMessages: boolean) {
    if (showAllMessages) {
      this.form?.markAllAsTouched();
    }
    this.formErrorMessages.set(FormHelper.generateFormErrorMessages(this.form));
  }

  addCustomErrorMessages(errorMessages: string[], removeExistingErrorMessages: boolean = true) {
    if (!removeExistingErrorMessages) {
      // We want to keep existing messages and add new ones to them
      errorMessages.push(...this.formErrorMessages());
    }
    this.formErrorMessages.set(errorMessages);
  }

  private static generateFormErrorMessages(formGroup: FormGroup): string[] {
    let formErrors: string[] = [];
    if (formGroup && !formGroup?.valid) {
      Object.keys(formGroup.controls).forEach(controlName => {
        let control = formGroup.controls[controlName];
        let controlErrors: string[] = this.generateControlErrorMessages(controlName, control);
        if (controlErrors?.length > 0) {
          formErrors.push(...controlErrors);
        }
      });
    }
    return formErrors;
  }

  private static generateControlErrorMessages(controlName: string, control: AbstractControl<any, any, any>): string[] {
    if (!control?.errors
      || !(control?.touched || control?.dirty)
    ) {
      return null;
    }

    let controlErrors: string[] = [];
    Object.keys(control?.errors).forEach(errorName => {
      switch (errorName) {
        case 'required': {
          controlErrors.push(`${controlName} is required.`);
          break;
        }
        case 'email': {
          controlErrors.push(`${controlName} is not a valid email.`);
          break;
        }
        case 'pattern': {
          controlErrors.push(`${controlName} is not of the correct format.`);
          break;
        }
        case 'minlength': {
          controlErrors.push(`${controlName} minimum length is ${control.getError('minlength')['requiredLength' as keyof {}]}.`);
          break;
        }
        case 'maxlength': {
          controlErrors.push(`${controlName} maximum length is ${control.getError('maxlength')['requiredLength' as keyof {}]}.`);
          break;
        }
        case 'requiredTrue': {
          controlErrors.push(`${controlName} must be selected.`);
          break;
        }
        case 'min': {
          controlErrors.push(`${controlName} minimum value is ${control.getError('min')['min' as keyof {}]}.`);
          break;
        }
        case 'max': {
          controlErrors.push(`${controlName} maximum value is ${control.getError('max')['max' as keyof {}]}.`);
          break;
        }
        default: {
          controlErrors.push(`${controlName} is not valid :${errorName}.`);
          break;
        }
      }
    });
    return controlErrors;
  }

}
